import React from "react";
import { useQuery, useAction } from "wasp/client/operations";
import {
  getAccommodationById,
  deleteAccommodation,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  ArrowLeft,
  Share2,
  Calendar,
  MapPin,
  Phone,
  User,
  Edit3,
  Trash2,
  Home,
  BedDouble,
  Bath,
  Tv,
  Mail,
} from "lucide-react";
import { SubscribeButton } from "../content/components/SubscribeButton";
import { EnhancedIconButton } from "../../components/ui/enhanced-page-layout";

export default function ViewAccommodationPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const deleteAccommodationFn = useAction(deleteAccommodation);

  const {
    data: item,
    isLoading,
    error,
  } = useQuery(
    getAccommodationById,
    { itemId: itemId || "" },
    {
      enabled: !!itemId,
    },
  );

  const handleBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/accommodation`);
    } else {
      navigate("/accommodation");
    }
  };

  const handleDelete = async () => {
    if (
      !itemId ||
      !window.confirm("Are you sure you want to delete this listing?")
    )
      return;

    try {
      await deleteAccommodationFn({ itemId });
      handleBack();
    } catch (error) {
      console.error("Error deleting listing:", error);
      alert("Failed to delete listing");
    }
  };

  const getTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      APARTMENT: "Apartment/Flat",
      HOUSE: "House",
      ROOM: "Room Only",
      SHARED: "Shared",
      HOSTEL: "Hostel/Dorm",
      OTHER: "Other",
    };
    return types[type] || type;
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-12 text-center text-gray-500">
          Access Denied. Please log in.
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !item) {
    return (
      <DashboardLayout>
        <div className="p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Listing Not Found
          </h3>
          <button
            onClick={handleBack}
            className="text-blue-600 hover:underline"
          >
            Return to listings
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const isAuthor = user.id === item.authorId;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-4 md:p-6 lg:p-8">
        {/* Header Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Listings</span>
          </button>

          <div className="flex items-center gap-2">
            {/* Share Button (Mock) */}
            <EnhancedIconButton
              icon={Share2}
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert("Link copied to clipboard!");
              }}
              title="Share listing"
            />

            {isAuthor && (
              <>
                <EnhancedIconButton
                  icon={Edit3}
                  onClick={() => {
                    if (selectedLocation?.locationCode) {
                      navigate(
                        `/locations/${selectedLocation.locationCode}/accommodation/${itemId}/edit`,
                      );
                    } else {
                      navigate(`/accommodation/${itemId}/edit`);
                    }
                  }}
                  title="Edit listing"
                />
                <EnhancedIconButton
                  icon={Trash2}
                  variant="danger"
                  onClick={handleDelete}
                  title="Delete listing"
                />
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Title Section */}
          <div className="p-6 md:p-8 border-b border-gray-100">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full uppercase tracking-wide">
                    {getTypeLabel(item.metadata?.type)}
                  </span>
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Posted {new Date(item.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {item.title}
                  </h1>
                  <SubscribeButton contentId={item.id} />
                </div>
                <div className="flex items-center gap-2 text-gray-600 mt-2">
                  <MapPin className="w-4 h-4" />
                  <span>
                    {item.metadata?.address || "Location provided upon contact"}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-green-600">
                  ${item.metadata?.price}
                </div>
                <div className="text-sm text-gray-500">
                  per {item.metadata?.period?.toLowerCase()}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">
            {/* Left Column: Details & Description */}
            <div className="col-span-2 p-6 md:p-8 space-y-8">
              {/* Key Features */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {item.metadata?.bedrooms && (
                  <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg text-center">
                    <BedDouble className="w-6 h-6 text-blue-500 mb-2" />
                    <span className="text-lg font-semibold text-gray-900">
                      {item.metadata.bedrooms}
                    </span>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">
                      Bedrooms
                    </span>
                  </div>
                )}
                {item.metadata?.bathrooms && (
                  <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg text-center">
                    <Bath className="w-6 h-6 text-blue-500 mb-2" />
                    <span className="text-lg font-semibold text-gray-900">
                      {item.metadata.bathrooms}
                    </span>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">
                      Bathrooms
                    </span>
                  </div>
                )}
                <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg text-center">
                  <Tv
                    className={`w-6 h-6 mb-2 ${item.metadata?.furnished
                      ? "text-blue-500"
                      : "text-gray-400"
                      }`}
                  />
                  <span className="text-lg font-semibold text-gray-900">
                    {item.metadata?.furnished ? "Yes" : "No"}
                  </span>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">
                    Furnished
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg text-center">
                  <Home className="w-6 h-6 text-blue-500 mb-2" />
                  <span className="text-sm font-semibold text-gray-900 break-words w-full px-1">
                    {getTypeLabel(item.metadata?.type)}
                  </span>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">
                    Type
                  </span>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Description
                </h3>
                <div className="prose prose-blue max-w-none text-gray-600 whitespace-pre-line">
                  {item.description}
                </div>
              </div>

              {/* Availability */}
              {item.metadata?.availableFrom && (
                <div className="flex items-center gap-3 p-4 bg-yellow-50 text-yellow-800 rounded-lg border border-yellow-100">
                  <Calendar className="w-5 h-5" />
                  <span className="font-medium">
                    Available from:{" "}
                    {new Date(item.metadata.availableFrom).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>

            {/* Right Column: Contact info */}
            <div className="p-6 md:p-8 bg-gray-50/50">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-6">
                Contact Information
              </h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-white p-2 rounded-full shadow-sm">
                    <User className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {item.author.name || item.author.username || "User"}
                    </div>
                    <div className="text-sm text-gray-500">Property Owner</div>
                  </div>
                </div>

                {item.metadata?.email && (
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 text-gray-900 mb-1">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">Email</span>
                    </div>
                    <div className="pl-8 text-gray-600 break-all">
                      <a href={`mailto:${item.metadata.email}`} className="text-blue-600 hover:underline">
                        {item.metadata.email}
                      </a>
                    </div>
                  </div>
                )}

                {item.metadata?.phoneNumber && (
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 text-gray-900 mb-1">
                      <Phone className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">Phone</span>
                    </div>
                    <div className="pl-8 text-gray-600 break-all">
                      <a href={`tel:${item.metadata.phoneNumber}`} className="text-blue-600 hover:underline">
                        {item.metadata.phoneNumber}
                      </a>
                    </div>
                  </div>
                )}

                {item.metadata?.contactInfo && !item.metadata?.email && !item.metadata?.phoneNumber && (
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 text-gray-900 mb-1">
                      <Phone className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">Contact Details</span>
                    </div>
                    <div className="pl-8 text-gray-600 break-all">
                      {item.metadata.contactInfo}
                    </div>
                  </div>
                )}

                <div className="text-xs text-gray-500 mt-8 pt-8 border-t border-gray-200">
                  <p className="mb-2">
                    <strong>Safety Tips:</strong>
                  </p>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>Always visit the property in person.</li>
                    <li>Never transfer money before signing a contract.</li>
                    <li>Meet in a public place if possible.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
